import { CSSProperties, memo, ReactNode, useMemo } from 'react';
import { Pictogram, PictogramName } from '@cbhq/cds-web/illustrations';
import { VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextTitle1 } from '@cbhq/cds-web/typography';

import { useIsRetailTheme } from '../hooks/useIsRetailTheme';

type ContentHeaderProps = {
  picName?: PictogramName;
  title?: ReactNode;
  description?: string | string[] | ReactNode;
  isStorybookPrototype?: boolean;
  centerContent?: boolean;
};

const storybookPrototypeWrapperStyle = { backgroundColor: '#0000001a' } as CSSProperties;

// Retail treatment matches the CDS SectionHeader title→description gap (`gap={0.5}`, 4px).
const RETAIL_TITLE_DESCRIPTION_GAP = 0.5;
const DEFAULT_GAP = 1;

export const ScreenHeader = memo(function ScreenHeader({
  picName,
  title,
  description,
  isStorybookPrototype,
  centerContent,
}: ContentHeaderProps) {
  const centerContentText = centerContent ? 'center' : undefined;
  const isRetailTheme = useIsRetailTheme();

  const descriptionContent = useMemo(() => {
    if (!description || (Array.isArray(description) && description.length === 0)) return null;

    if (typeof description === 'string') {
      return (
        <TextBody
          as="p"
          color="fgMuted"
          overflow="break"
          display="block"
          textAlign={centerContentText}
        >
          {description}
        </TextBody>
      );
    }

    if (Array.isArray(description)) {
      return description.map((line) => (
        <TextBody
          key={line}
          as="p"
          color="fgMuted"
          overflow="break"
          display="block"
          textAlign={centerContentText}
        >
          {line}
        </TextBody>
      ));
    }

    // For ReactNode (JSX elements, etc.)
    return (
      <TextBody as="div" color="fgMuted" overflow="break" textAlign={centerContentText}>
        {description}
      </TextBody>
    );
  }, [description, centerContentText]);

  return (
    <VStack
      gap={DEFAULT_GAP}
      style={isStorybookPrototype ? storybookPrototypeWrapperStyle : undefined}
      testID="screen-header"
      alignItems={centerContentText}
    >
      {picName && (
        <VStack paddingBottom={1}>
          <Pictogram name={picName} testID="screen-header-pictogram" />
        </VStack>
      )}
      {isRetailTheme ? (
        (title || descriptionContent) && (
          <VStack
            gap={RETAIL_TITLE_DESCRIPTION_GAP}
            alignItems={centerContentText}
            testID="screen-header-text"
          >
            {title && (
              <TextTitle1 as="p" display="block">
                {title}
              </TextTitle1>
            )}
            {descriptionContent && (
              <VStack gap={DEFAULT_GAP} alignItems={centerContentText}>
                {descriptionContent}
              </VStack>
            )}
          </VStack>
        )
      ) : (
        <>
          {title && (
            <TextTitle1 as="p" display="block">
              {title}
            </TextTitle1>
          )}
          {descriptionContent}
        </>
      )}
    </VStack>
  );
});
